import Action, { type AnimationStep } from './Action';
import ProjectedOccupancy from './ProjectedOccupancy';
import type { Proposal } from './Proposal';
import { isProposalProvider } from '../types/Components';
import { ActorStats, Faction, HexPosition, PlayerControlled } from '../components';
import HexBearing from '../grid/HexBearing';
import type { AttackEffect } from './Proposal';

/**
 * Gathers all proposals from components for a given list of actions.
 */
export function gatherProposals(actions: Action[]): { action: Action, proposal: Proposal }[] {
  const allProposals: { action: Action, proposal: Proposal }[] = [];
  for (const action of actions) {
    for (const component of action.entity.getAllComponents()) {
      if (isProposalProvider(component)) {
        const proposal = component.getProposedOutcome(action);
        if (proposal) {
          allProposals.push({ action, proposal });
        }
      }
    }
  }
  return allProposals;
}

/**
 * Merges multiple animation plans into a single plan based on strategies.
 */
export function buildAnimationPlan(contributingAnimations: { plan: AnimationStep[][], strategy?: string }[]): AnimationStep[][] {
  let finalPlan: AnimationStep[][] = [];

  const replacer = contributingAnimations.find(a => a.strategy === 'REPLACE');
  if (replacer) {
    finalPlan = JSON.parse(JSON.stringify(replacer.plan));
  } else {
    const base = contributingAnimations[0];
    if (base) {
      finalPlan = JSON.parse(JSON.stringify(base.plan));
    }
  }

  const appenders = contributingAnimations.filter(a => a.strategy === 'APPEND');
  for (const appender of appenders) {
    for (let i = 0; i < appender.plan.length; i++) {
      if (!finalPlan[i]) finalPlan[i] = [];
      finalPlan[i].push(...appender.plan[i]);
    }
  }

  return finalPlan;
}

/**
 * Determines the winner among a list of actions based on proposal priority and tie-breaking rules.
 */
export function determineWinner(actions: Action[], allProposals: { action: Action, proposal: Proposal }[]): Action {
  if (actions.length === 1) return actions[0];

  const actionProposals = allProposals.filter(p => actions.includes(p.action));

  if (actionProposals.length === 0) {
    return resolveTie(actions);
  }

  const highestPriority = Math.max(...actionProposals.map(p => p.proposal.priority));
  const topPriorityProposals = actionProposals.filter(p => p.proposal.priority === highestPriority);
  const topActions = [...new Set(topPriorityProposals.map(p => p.action))];

  if (topActions.length === 1) {
    return topActions[0];
  }
  return resolveTie(topActions);
}

/**
 * Resolves a tie between actions using speed, player advantage, and bearing.
 */
export function resolveTie(actions: Action[]): Action {
  // 1. Tie-break by speed
  let maxSpeed = -1;
  actions.forEach(action => {
    const speed = action.entity.getComponent(ActorStats)!.speed;
    if (speed > maxSpeed) maxSpeed = speed;
  });
  const speedWinners = actions.filter(action => action.entity.getComponent(ActorStats)!.speed === maxSpeed);

  if (speedWinners.length === 1) {
    return speedWinners[0];
  }

  // 2. Tie-break by PlayerControlled
  const playerWinner = speedWinners.find(action => action.entity.hasComponent(PlayerControlled));
  if (playerWinner) {
    return playerWinner;
  }

  // 3. Tie-break by Bearing for AI vs AI.
  return speedWinners.reduce((winner, challenger) => {
    const winnerBearing = winner.entity.getComponent(HexPosition)!.bearing;
    const challengerBearing = challenger.entity.getComponent(HexPosition)!.bearing;

    if (winnerBearing === challengerBearing) {
      return winner;
    }

    const newWinnerBearing = HexBearing.getClockwiseWinner(winnerBearing, challengerBearing);
    return newWinnerBearing === challengerBearing ? challenger : winner;
  });
}

export type Phase2AttackOutcome =
  | { type: 'HIT'; target: any }
  | { type: 'NEAR_MISS' }
  | { type: 'WHIFF' };

/**
 * Calculates the outcome of a phase 2 attack (END or BOTH timing).
 */
export function getPhase2AttackOutcome(
  effect: AttackEffect,
  action: Action,
  initialOccupancy: ProjectedOccupancy,
  finalOccupancy: ProjectedOccupancy
): Phase2AttackOutcome {
  let target: any | undefined;
  let wasNearMiss = false;

  const finalOccupant = finalOccupancy.getOccupant(effect.attackHex);
  const initialOccupant = initialOccupancy.getOccupant(effect.attackHex);

  const finalEnemy = (finalOccupant && finalOccupant.getComponent(Faction)?.name !== action.entity.getComponent(Faction)?.name) ? finalOccupant : null;
  const initialEnemy = (initialOccupant && initialOccupant.getComponent(Faction)?.name !== action.entity.getComponent(Faction)?.name) ? initialOccupant : null;

  const timing = effect.attackTiming || 'END';

  if (timing === 'END') {
    if (finalEnemy) target = finalEnemy;
  } else if (timing === 'ANY') {
    if (initialEnemy) target = initialEnemy;
    else if (finalEnemy) target = finalEnemy;
  } else if (timing === 'BOTH') {
    if (initialEnemy && finalEnemy && initialEnemy.id === finalEnemy.id) {
      target = finalEnemy;
    }
  }

  if (initialEnemy && initialEnemy.id !== action.entity.id) {
    wasNearMiss = true;
  }

  if (target) {
    return { type: 'HIT', target };
  } else if (wasNearMiss) {
    return { type: 'NEAR_MISS' };
  } else {
    return { type: 'WHIFF' };
  }
}
